'use client';

import { useState, useEffect } from 'react';
import { cn } from '@/src/shared/lib/utils';

interface AttachmentPreviewProps {
  file: File;
  onRemove: () => void;
  uploadProgress?: number;
  error?: string;
}

export const AttachmentPreview = ({
  file,
  onRemove,
  uploadProgress,
  error,
}: AttachmentPreviewProps) => {
  const isImage = file.type.startsWith('image/');
  const [preview, setPreview] = useState<string | null>(null);

  useEffect(() => {
    if (isImage) {
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target?.result as string);
      reader.readAsDataURL(file);
    }
  }, [file, isImage]);

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div
      className={cn(
        'relative p-2 border rounded-lg',
        error ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-gray-50'
      )}
    >
      {isImage && preview && (
        <div className="relative w-20 h-20 rounded overflow-hidden">
          <img
            src={preview}
            alt={file.name}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      {!isImage && (
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 bg-gray-200 rounded flex items-center justify-center">
            📄
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{file.name}</p>
            <p className="text-xs text-gray-500">
              {formatFileSize(file.size)}
            </p>
          </div>
        </div>
      )}

      {uploadProgress !== undefined && uploadProgress < 100 && (
        <div className="mt-2">
          <div className="h-1 bg-gray-200 rounded overflow-hidden">
            <div
              className="h-full bg-accent-primary transition-all"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        </div>
      )}

      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}

      <button
        type="button"
        onClick={onRemove}
        className="absolute top-1 right-1 p-1 bg-white rounded-full shadow hover:bg-gray-100"
        aria-label="Remove file"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  );
};
